import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import {
  HelpCircle,
  ExternalLink,
  BookOpen,
  Video,
  FileText,
  MessageCircle,
  X,
  ChevronRight,
  Search,
  Sparkles,
  Send,
  Bot,
  User,
  Loader2,
  AlertCircle,
  MapPin,
} from 'lucide-react';
import { HELP_ARTICLES } from '../config/onboardingMedia';
import { HELP_TOPICS } from '../config/helpContent';
import { getPageContext, searchFieldGlossary, getFieldDefinition, FIELD_GLOSSARY } from '../config/fieldGlossary';
import api from '../utils/api';

/**
 * HelpCenterLink - Inline help link component
 *
 * Shows contextual help links to documentation and videos.
 */
export const HelpCenterLink = ({
  articleId,
  inline = false,
  showIcon = true,
  children,
}) => {
  const article = HELP_ARTICLES[articleId];

  if (!article) {
    return children || null;
  }

  if (inline) {
    return (
      <a
        href={article.url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline"
      >
        {showIcon && <HelpCircle className="w-3 h-3" />}
        {children || article.title}
        <ExternalLink className="w-3 h-3" />
      </a>
    );
  }

  return (
    <a
      href={article.url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
    >
      <BookOpen className="w-4 h-4" />
      <span className="text-sm">{children || article.title}</span>
      <ExternalLink className="w-3 h-3 ml-auto" />
    </a>
  );
};

/**
 * HelpCenterButton - Floating help button that opens the help panel
 */
export const HelpCenterButton = ({ position = 'bottom-right' }) => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const positionClasses = {
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
  };

  // Get page context based on current route
  const pageContext = getPageContext(location.pathname);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`fixed ${positionClasses[position]} z-40 p-3 rounded-full bg-blue-600 text-white shadow-lg hover:bg-blue-700 hover:scale-110 transition-all`}
        aria-label="Open help center"
      >
        <HelpCircle className="w-6 h-6" />
      </button>

      {open && (
        <HelpCenterPanel
          onClose={() => setOpen(false)}
          pageContext={pageContext}
          currentPath={location.pathname}
        />
      )}
    </>
  );
};

/**
 * Simple markdown-like formatting for chat messages
 */
const formatMessageContent = (content) => {
  if (!content) return null;

  // Split by lines and process
  const lines = content.split('\n');
  const elements = [];

  lines.forEach((line, i) => {
    // Bold text: **text**
    const formattedLine = line.split(/(\*\*[^*]+\*\*)/).map((part, j) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={j} className="font-semibold">{part.slice(2, -2)}</strong>;
      }
      return part;
    });

    // Numbered list items
    const numberedMatch = line.match(/^(\d+)\.\s+(.+)$/);
    if (numberedMatch) {
      elements.push(
        <div key={i} className="flex gap-2 ml-2">
          <span className="text-slate-400 flex-shrink-0">{numberedMatch[1]}.</span>
          <span>{formattedLine}</span>
        </div>
      );
    }
    // Horizontal rule
    else if (line.trim() === '---') {
      elements.push(<hr key={i} className="my-2 border-slate-200 dark:border-slate-600" />);
    }
    // Empty line
    else if (line.trim() === '') {
      elements.push(<div key={i} className="h-2" />);
    }
    // Regular line
    else {
      elements.push(<div key={i}>{formattedLine}</div>);
    }
  });

  return elements;
};

/**
 * ChatMessage - Individual chat message component
 */
const ChatMessage = ({ message }) => {
  const isUser = message.role === 'user';

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[85%] rounded-lg p-3 ${
        isUser
          ? 'bg-blue-600 text-white'
          : message.isError
            ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
            : 'bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200'
      }`}>
        {/* Message icon */}
        <div className="flex items-start gap-2">
          {!isUser && (
            <div className="flex-shrink-0 mt-0.5">
              {message.isSmartResponse ? (
                <Search className="w-4 h-4 text-blue-500" />
              ) : (
                <Bot className={`w-4 h-4 ${message.isError ? 'text-red-500' : 'text-blue-500'}`} />
              )}
            </div>
          )}
          <div className="flex-1 min-w-0">
            {/* Smart response indicator */}
            {message.isSmartResponse && (
              <div className="text-xs text-slate-400 dark:text-slate-500 mb-1 italic">
                From documentation
              </div>
            )}

            {/* Render message content with basic formatting */}
            <div className="text-sm break-words">
              {formatMessageContent(message.content)}
            </div>

            {/* Related fields (for smart responses) */}
            {message.relatedFields?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-600">
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">Related fields:</p>
                {message.relatedFields.map((field, i) => (
                  <div key={i} className="text-xs text-slate-600 dark:text-slate-300 mb-1">
                    <span className="font-medium">{field.label}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Related articles */}
            {message.relatedArticles?.length > 0 && (
              <div className={`mt-3 pt-3 border-t border-slate-200 dark:border-slate-600 ${message.relatedFields?.length > 0 ? 'mt-2 pt-2' : ''}`}>
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">Related articles:</p>
                {message.relatedArticles.map((article, i) => (
                  <a
                    key={i}
                    href={article.url}
                    className="block text-xs text-blue-600 dark:text-blue-400 hover:underline mb-1"
                  >
                    {article.title}
                  </a>
                ))}
              </div>
            )}

            {/* Watermark */}
            {message.watermark && (
              <p className="mt-2 text-xs text-slate-400 dark:text-slate-500">{message.watermark}</p>
            )}
          </div>
          {isUser && (
            <User className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-200" />
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * TypingIndicator - Loading indicator for AI response
 */
const TypingIndicator = () => (
  <div className="flex justify-start">
    <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-3 flex items-center gap-2">
      <Bot className="w-4 h-4 text-blue-500" />
      <Loader2 className="w-4 h-4 animate-spin text-slate-500" />
      <span className="text-sm text-slate-500">Thinking...</span>
    </div>
  </div>
);

/**
 * Search help topics for relevant matches
 */
const searchHelpTopics = (query, limit = 3) => {
  if (!query || !HELP_TOPICS) return [];

  const queryLower = query.toLowerCase();
  const queryTokens = queryLower.split(/\s+/).filter(Boolean);

  const scored = HELP_TOPICS
    .map(topic => {
      const searchable = [
        topic.label || '',
        topic.description || '',
        ...(topic.keywords || []),
        ...(topic.steps || []),
      ].join(' ').toLowerCase();

      const score = queryTokens.reduce((acc, token) =>
        acc + (searchable.includes(token) ? 1 : 0), 0);

      return { topic, score };
    })
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);

  return scored.map(({ topic }) => topic.key);
};

/**
 * Get full topic objects for given keys
 */
const getTopicsByKeys = (keys) => {
  if (!keys || !HELP_TOPICS) return [];
  return keys
    .map(key => HELP_TOPICS.find(t => t.key === key))
    .filter(Boolean);
};

/**
 * Generate a smart non-AI response based on search results
 * This provides helpful information even when AI is unavailable
 */
const generateSmartResponse = (query, pageContext) => {
  const queryLower = query.toLowerCase();

  // Search for matching fields
  const matchingFields = searchFieldGlossary(query, 3);

  // Search for matching help topics
  const topicKeys = searchHelpTopics(query, 3);
  const matchingTopics = getTopicsByKeys(topicKeys);

  // Build response parts
  const parts = [];

  // Check if asking about a specific field
  const fieldQuestionPatterns = [
    /what\s+(?:is|does|are)\s+(.+?)(?:\?|$)/i,
    /explain\s+(.+?)(?:\?|$)/i,
    /define\s+(.+?)(?:\?|$)/i,
    /meaning\s+of\s+(.+?)(?:\?|$)/i,
  ];

  let isFieldQuestion = false;
  for (const pattern of fieldQuestionPatterns) {
    if (pattern.test(query)) {
      isFieldQuestion = true;
      break;
    }
  }

  // If we found matching fields, provide their definitions
  if (matchingFields.length > 0) {
    if (isFieldQuestion || matchingFields[0].label.toLowerCase().includes(queryLower.replace(/[?]/g, '').trim())) {
      // Direct field match - provide detailed info
      const field = matchingFields[0];
      parts.push(`**${field.label}**`);
      parts.push(field.description);
      if (field.calculation) {
        parts.push(`\n**Calculation:** ${field.calculation}`);
      }
      if (field.relatedFields?.length > 0) {
        const relatedLabels = field.relatedFields
          .map(key => {
            const related = getFieldDefinition(key);
            return related ? related.label : key;
          })
          .slice(0, 3);
        parts.push(`\n**Related fields:** ${relatedLabels.join(', ')}`);
      }
    } else if (matchingFields.length > 1) {
      // Multiple potential matches
      parts.push('Here are some relevant field definitions:');
      matchingFields.slice(0, 3).forEach(field => {
        parts.push(`\n**${field.label}:** ${field.description}`);
      });
    }
  }

  // If we found matching help topics, provide step-by-step guidance
  if (matchingTopics.length > 0) {
    const topic = matchingTopics[0];

    if (parts.length > 0) {
      parts.push('\n---\n');
    }

    if (topic.steps?.length > 0) {
      parts.push(`**How to: ${topic.label}**`);
      topic.steps.forEach((step, i) => {
        parts.push(`${i + 1}. ${step}`);
      });
    } else if (topic.description) {
      parts.push(`**${topic.label}**`);
      parts.push(topic.description);
    }

    if (topic.tips?.length > 0) {
      parts.push(`\n**Tip:** ${topic.tips[0]}`);
    }
  }

  // If no matches found, provide helpful fallback
  if (parts.length === 0) {
    if (pageContext) {
      parts.push(`I couldn't find specific information about "${query}".`);
      parts.push(`\nYou're currently on the **${pageContext.name}** page.`);
      if (pageContext.commonFields?.length > 0) {
        const fieldLabels = pageContext.commonFields
          .map(key => {
            const field = getFieldDefinition(key);
            return field ? field.label : null;
          })
          .filter(Boolean)
          .slice(0, 5);
        if (fieldLabels.length > 0) {
          parts.push(`Common fields on this page include: ${fieldLabels.join(', ')}.`);
          parts.push('\nTry asking about one of these fields!');
        }
      }
    } else {
      parts.push(`I couldn't find specific information about "${query}".`);
      parts.push('\nTry browsing the Help articles or contact support for assistance.');
    }
  }

  // Build related articles list
  const relatedArticles = matchingTopics.slice(0, 3).map(topic => ({
    key: topic.key,
    title: topic.label,
    url: `/help?topic=${topic.key}`,
  }));

  // Build related fields list
  const relatedFields = matchingFields.slice(0, 3).map(field => ({
    key: field.key,
    label: field.label,
    description: field.description,
  }));

  return {
    content: parts.join('\n'),
    relatedArticles,
    relatedFields,
    isSmartResponse: true,
  };
};

/**
 * HelpCenterPanel - Slide-out help center panel with AI Assistant
 */
export const HelpCenterPanel = ({ onClose, initialArticle, pageContext, currentPath }) => {
  const [activeTab, setActiveTab] = useState('browse'); // 'browse' | 'ai'
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedArticle, setSelectedArticle] = useState(initialArticle);

  // AI Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatContainerRef = useRef(null);

  // AI availability state
  const [aiStatus, setAiStatus] = useState({ available: null, reason: null, loading: true });

  const articles = Object.entries(HELP_ARTICLES).map(([id, article]) => ({
    id,
    ...article,
  }));

  const filteredArticles = searchQuery
    ? articles.filter(a =>
        a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        a.articleId.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : articles;

  const popularTopics = [
    { id: 'getting-started', title: 'Getting Started', icon: BookOpen },
    { id: 'global-search', title: 'Using Search', icon: Search },
    { id: 'budget-transfers', title: 'Budget Transfers', icon: FileText },
  ];

  // Check AI availability when panel opens
  useEffect(() => {
    const checkAiStatus = async () => {
      try {
        const res = await api.get('/api/help/status');
        setAiStatus({
          available: res.data.available,
          reason: res.data.reason,
          loading: false,
        });
      } catch (err) {
        // If status check fails, assume AI is unavailable
        setAiStatus({
          available: false,
          reason: 'Unable to check AI availability',
          loading: false,
        });
      }
    };
    checkAiStatus();
  }, []);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatMessages, chatLoading]);

  // Handle AI chat submission (or smart search fallback when AI unavailable)
  const handleAskAI = async () => {
    if (!chatInput.trim() || chatLoading) return;

    const userMessage = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setChatLoading(true);

    // If AI is unavailable, use smart local search instead
    if (!aiStatus.available) {
      // Small delay to feel more natural
      await new Promise(resolve => setTimeout(resolve, 300));

      const smartResponse = generateSmartResponse(userMessage, pageContext);
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: smartResponse.content,
        relatedArticles: smartResponse.relatedArticles,
        relatedFields: smartResponse.relatedFields,
        isSmartResponse: true,
      }]);
      setChatLoading(false);
      return;
    }

    try {
      // Find relevant topic keys to send to backend
      const topicKeys = searchHelpTopics(userMessage, 3);

      // Search field glossary for relevant field definitions
      const matchingFields = searchFieldGlossary(userMessage, 3);
      const fieldKeys = matchingFields.map(f => f.key);

      const res = await api.post('/api/help/chat', {
        question: userMessage,
        topic_keys: topicKeys,
        field_keys: fieldKeys,
        // Send page context so AI knows where user is
        page_context: pageContext ? {
          path: currentPath,
          name: pageContext.name,
          description: pageContext.description,
          commonFields: pageContext.commonFields,
        } : null,
      });

      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: res.data.answer,
        relatedArticles: res.data.related_articles,
        watermark: res.data.watermark,
      }]);
    } catch (err) {
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: err.response?.data?.error || 'Sorry, I encountered an error. Please try again or contact support.',
        isError: true,
      }]);
    } finally {
      setChatLoading(false);
    }
  };

  // Handle Enter key in chat input
  const handleChatKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAskAI();
    }
  };

  const content = (
    <div className="fixed inset-0 z-[200]">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        role="button"
        tabIndex={0}
        onClick={onClose}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ' || e.key === 'Escape') && onClose()}
        aria-label="Close help panel"
      />

      {/* Panel */}
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-white dark:bg-slate-900 shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
              <HelpCircle className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="font-semibold text-slate-800 dark:text-slate-100">
                Help Center
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Find answers and learn more
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-slate-200 dark:border-slate-700">
          <button
            onClick={() => setActiveTab('browse')}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              activeTab === 'browse'
                ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
          >
            <BookOpen className="w-4 h-4 inline mr-2" />
            Browse Help
          </button>
          <button
            onClick={() => setActiveTab('ai')}
            disabled={aiStatus.loading}
            className={`flex-1 py-3 text-sm font-medium transition-colors relative ${
              activeTab === 'ai'
                ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
            } ${aiStatus.loading ? 'opacity-50' : ''}`}
          >
            {aiStatus.loading ? (
              <Loader2 className="w-4 h-4 inline mr-2 animate-spin" />
            ) : aiStatus.available ? (
              <Sparkles className="w-4 h-4 inline mr-2" />
            ) : (
              <Search className="w-4 h-4 inline mr-2" />
            )}
            {aiStatus.available ? 'AI Assistant' : 'Smart Search'}
          </button>
        </div>

        {/* Browse Tab Content */}
        {activeTab === 'browse' && (
          <>
            {/* Search */}
            <div className="p-4 border-b border-slate-200 dark:border-slate-700">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search help articles..."
                  className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {!searchQuery && (
                <>
                  {/* Quick actions */}
                  <div className="p-4 border-b border-slate-200 dark:border-slate-700">
                    <div className="grid grid-cols-2 gap-2">
                      <a
                        href="/help"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-blue-500 dark:hover:border-blue-500 transition-colors"
                      >
                        <BookOpen className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                          Documentation
                        </span>
                      </a>
                      <a
                        href="/help/videos"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-blue-500 dark:hover:border-blue-500 transition-colors"
                      >
                        <Video className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                          Video Tutorials
                        </span>
                      </a>
                      <a
                        href="/help/contact"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-blue-500 dark:hover:border-blue-500 transition-colors"
                      >
                        <MessageCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                          Contact Support
                        </span>
                      </a>
                      <a
                        href="/help/faq"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-blue-500 dark:hover:border-blue-500 transition-colors"
                      >
                        <HelpCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                          FAQs
                        </span>
                      </a>
                    </div>
                  </div>

                  {/* Popular topics */}
                  <div className="p-4">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-3">
                      Popular Topics
                    </h3>
                    <div className="space-y-2">
                      {popularTopics.map(topic => {
                        const article = articles.find(a => a.articleId === topic.id);
                        const Icon = topic.icon;
                        return (
                          <a
                            key={topic.id}
                            href={article?.url || `/help/${topic.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                          >
                            <Icon className="w-5 h-5 text-slate-500 dark:text-slate-400" />
                            <span className="flex-1 text-sm text-slate-700 dark:text-slate-300">
                              {topic.title}
                            </span>
                            <ChevronRight className="w-4 h-4 text-slate-400" />
                          </a>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}

              {/* Search results or all articles */}
              <div className="p-4">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-3">
                  {searchQuery ? 'Search Results' : 'All Articles'}
                </h3>
                <div className="space-y-1">
                  {filteredArticles.length === 0 ? (
                    <p className="text-sm text-slate-500 dark:text-slate-400 py-4 text-center">
                      No articles found for "{searchQuery}"
                    </p>
                  ) : (
                    filteredArticles.map(article => (
                      <a
                        key={article.id}
                        href={article.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors group"
                      >
                        <FileText className="w-4 h-4 text-slate-400 group-hover:text-blue-500" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-slate-700 dark:text-slate-300 group-hover:text-blue-600 dark:group-hover:text-blue-400 truncate">
                            {article.title}
                          </p>
                        </div>
                        {article.videoUrl && (
                          <Video className="w-4 h-4 text-purple-500" title="Has video" />
                        )}
                        <ExternalLink className="w-3 h-3 text-slate-400 opacity-0 group-hover:opacity-100" />
                      </a>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-slate-200 dark:border-slate-700">
              <p className="text-xs text-slate-500 dark:text-slate-400 text-center">
                Can't find what you're looking for?{' '}
                <a
                  href="/help/contact"
                  className="text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Contact support
                </a>
              </p>
            </div>
          </>
        )}

        {/* AI Assistant Tab Content */}
        {activeTab === 'ai' && (
          <div className="flex-1 flex flex-col min-h-0">
            {/* Chat messages */}
            <div
              ref={chatContainerRef}
              className="flex-1 overflow-y-auto p-4 space-y-4"
            >
              {chatMessages.length === 0 && (
                <div className="text-center py-8">
                  {/* Show different header based on AI availability */}
                  {!aiStatus.loading && !aiStatus.available ? (
                    <>
                      <Search className="w-12 h-12 mx-auto mb-3 text-blue-500" />
                      <p className="font-medium text-slate-800 dark:text-slate-100 mb-1">
                        Smart Help Search
                      </p>
                      <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">
                        Search our help documentation and field glossary for answers.
                      </p>
                      <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 text-xs mb-4">
                        <AlertCircle className="w-3 h-3" />
                        AI assistant unavailable - using smart search
                      </div>
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-12 h-12 mx-auto mb-3 text-blue-500" />
                      <p className="font-medium text-slate-800 dark:text-slate-100 mb-1">
                        Ask me anything!
                      </p>
                      <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                        I can help you navigate Uni-Fund and provide step-by-step guidance.
                      </p>
                    </>
                  )}

                  {/* Show current page context */}
                  {pageContext && (
                    <div className="mb-4 px-3 py-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-left">
                      <div className="flex items-center gap-2 text-xs text-blue-600 dark:text-blue-400">
                        <MapPin className="w-3 h-3" />
                        <span className="font-medium">You're on: {pageContext.name}</span>
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                        Ask about fields or features on this page!
                      </p>
                    </div>
                  )}

                  <div className="space-y-2">
                    <p className="text-xs font-medium text-slate-400 dark:text-slate-500 uppercase">
                      Try asking:
                    </p>
                    {/* Context-aware suggestions based on current page */}
                    {(pageContext?.name === 'Budget Overview' ? [
                      'What is Available Balance?',
                      'Explain the difference between Actuals and Encumbrances',
                      'How do I read this budget summary?',
                    ] : pageContext?.name === 'Position Budgeting' ? [
                      'What does FTE mean?',
                      'How is salary savings calculated?',
                      'Explain the position budget fields',
                    ] : pageContext?.name === 'Budget Transfers' ? [
                      'How do I create a budget transfer?',
                      'What happens after I submit a transfer?',
                      'Can I transfer between different funds?',
                    ] : [
                      'How do I create a budget transfer?',
                      'What does Available Balance mean?',
                      'How do I export data to Excel?',
                    ]).map((suggestion, i) => (
                      <button
                        key={i}
                        onClick={() => {
                          setChatInput(suggestion);
                        }}
                        className="block w-full text-left text-sm px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                      >
                        "{suggestion}"
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {chatMessages.map((msg, i) => (
                <ChatMessage key={i} message={msg} />
              ))}
              {chatLoading && <TypingIndicator />}
            </div>

            {/* Chat input */}
            <div className="p-4 border-t border-slate-200 dark:border-slate-700">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={handleChatKeyDown}
                  placeholder={aiStatus.available ? "Ask a question..." : "Search help topics and fields..."}
                  disabled={chatLoading}
                  className="flex-1 px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                />
                <button
                  onClick={handleAskAI}
                  disabled={chatLoading || !chatInput.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
              <p className="mt-2 text-xs text-slate-400 dark:text-slate-500 text-center">
                {aiStatus.available
                  ? 'AI responses are based on documentation and may not be 100% accurate.'
                  : 'Results are based on our help documentation and field glossary.'}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

/**
 * ContextualHelp - Help tooltip that appears on hover
 */
export const ContextualHelp = ({ articleId, children }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const article = HELP_ARTICLES[articleId];

  if (!article) {
    return children;
  }

  return (
    <div
      className="relative inline-block"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {children}

      {showTooltip && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700">
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 rotate-45 w-3 h-3 bg-white dark:bg-slate-800 border-r border-b border-slate-200 dark:border-slate-700" />

          <p className="text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
            {article.title}
          </p>

          <div className="flex items-center gap-2 mt-2">
            <a
              href={article.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
            >
              <BookOpen className="w-3 h-3" />
              Read docs
            </a>
            {article.videoUrl && (
              <a
                href={article.videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-purple-600 dark:text-purple-400 hover:underline"
              >
                <Video className="w-3 h-3" />
                Watch video
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HelpCenterLink;
